import React, { useState, useEffect, useMemo } from 'react';
import Grid3X3 from 'lucide-react/dist/esm/icons/grid-3x3.js';
import useSquareBoardData from '../../hooks/useSquareBoardData';
import useSquareGameConnection from '../../hooks/useSquareGameConnection';
import { usePurchaseNavigate } from '../../hooks/usePurchaseNavigate';
import { useAuth } from '../../contexts/AuthContext';
import { useContent } from '../../contexts/ContentContext';
import SquareGrid from '../SquareGrid';
import SquareGridCard from '../SquareGridCard';
import InlineTranslationEdit from '../InlineTranslationEdit';
import api, { apiRoutes } from '../../api';
import { getConfig } from '../../config/environment';

/**
 * SquareGridSection — self-contained square game board for the grid engine.
 *
 * Encapsulates all board logic (SignalR, polling, user-square fetching,
 * data normalization) so ANY grid layout can render the real interactive
 * board instead of the static placeholder.
 *
 * Props come from gridSectionProps in GameHomePage.jsx:
 *   mainDraw, editMode, handleTranslationUpdate, t, onLoginClick, squares (ignored — we fetch our own)
 */
export default function SquareGridSection({ mainDraw, editMode, handleTranslationUpdate, t, onLoginClick }) {
  const purchaseNavigate = usePurchaseNavigate(onLoginClick);
  const { c } = useContent();
  const { user } = useAuth();

  const totalSquares = mainDraw?.config?.gridSize || 100;

  // Real-time board data via polling + SignalR merge
  const [hubConnected, setHubConnected] = useState(false);
  const { squares: apiSquares, squareGameId, loading: squaresLoading, applyEvent } = useSquareBoardData(mainDraw, {
    signalRConnected: hubConnected,
  });

  // SignalR live events
  const { isConnected } = useSquareGameConnection(squareGameId, {
    onSquareHeld: (squareNumber, heldUntilUtc) => applyEvent({ type: 'held', squareNumber, heldUntilUtc }),
    onSquareReleased: (squareNumber) => applyEvent({ type: 'released', squareNumber }),
    onSquarePurchased: (squareNumber, memberMaskedName) => applyEvent({ type: 'purchased', squareNumber, memberMaskedName }),
    onWinnerAnnounced: (squareNumber, memberMaskedName) => applyEvent({ type: 'winner', squareNumber, memberMaskedName }),
  });
  useEffect(() => { setHubConnected(isConnected); }, [isConnected]);

  // User's own squares (for isMine styling)
  const [userSquares, setUserSquares] = useState([]);
  useEffect(() => {
    const fetchUserSquares = async () => {
      if (!user || !mainDraw?.id) {
        setUserSquares([]);
        return;
      }
      try {
        const { tlfId } = getConfig();
        const res = await api.get(apiRoutes.MemberTickets, {
          params: { organizationId: tlfId, drawId: mainDraw.id },
        });
        const tickets = res?.data?.payload?.tickets || res?.data?.tickets || [];
        const squareNumbers = tickets
          .filter(tk => tk.squareNumber || tk.square_number)
          .map(tk => tk.squareNumber ?? tk.square_number);
        setUserSquares(squareNumbers);
      } catch {
        setUserSquares([]);
      }
    };
    fetchUserSquares();
  }, [user, mainDraw?.id]);

  // Normalize API squares → renderable grid (merge-not-append, filter phantoms)
  const squares = useMemo(() => {
    if (apiSquares && apiSquares.length > 0) {
      return apiSquares
        .map(sq => {
          const status = sq.status || 'available';
          const squareNumber = typeof sq.number === 'number' ? sq.number : (parseInt(sq.number, 10) || 0);
          return {
            id: squareNumber,
            number: squareNumber,
            taken: status === 'taken' || status === 'sold',
            held: status === 'held' || status === 'reserved',
            status,
            isMine: userSquares.includes(squareNumber),
          };
        })
        .filter(sq => sq && typeof sq.number === 'number' && sq.number > 0);
    }
    return Array.from({ length: totalSquares }, (_, i) => ({
      id: i + 1,
      number: i + 1,
      taken: false,
      held: false,
      status: 'available',
      isMine: userSquares.includes(i + 1),
    }));
  }, [apiSquares, totalSquares, userSquares]);

  const squaresTaken = squares.filter(sq => sq.taken).length;
  const squaresHeld = squares.filter(sq => sq.held).length;
  const squaresRemaining = Math.max(0, totalSquares - squaresTaken - squaresHeld);

  const drawComplete = mainDraw?.winning_square || mainDraw?.winningSquare;
  const winningSquare = mainDraw?.winning_square ?? mainDraw?.winningSquare ?? null;

  return (
    <div className="py-4">
      <SquareGridCard available={squaresRemaining} total={totalSquares}>
        <SquareGrid
          squares={squares}
          gridSize={totalSquares}
          winningSquare={winningSquare}
          onSelect={(num) => purchaseNavigate(`/square-game-purchase?square=${num}`)}
          readOnly={!!drawComplete}
          variant="standard"
        />
      </SquareGridCard>
    </div>
  );
}
